import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const languages = [
  { 
    code: 'pt', 
    name: 'Português', 
    flag: (
      <svg width="24" height="16" viewBox="0 0 24 16" className="rounded-sm">
        <rect width="24" height="16" fill="#006600"/>
        <rect x="8" width="16" height="16" fill="#FF0000"/>
        <circle cx="8" cy="8" r="4" fill="#FFFF00"/>
        <circle cx="8" cy="8" r="2.5" fill="#006600"/>
      </svg>
    )
  },
  { 
    code: 'es', 
    name: 'Español', 
    flag: (
      <svg width="24" height="16" viewBox="0 0 24 16" className="rounded-sm">
        <rect width="24" height="16" fill="#c60b1e"/>
        <rect y="4" width="24" height="8" fill="#ffc400"/>
      </svg>
    )
  },
  { 
    code: 'en', 
    name: 'English', 
    flag: (
      <svg width="24" height="16" viewBox="0 0 24 16" className="rounded-sm">
        <rect width="24" height="16" fill="#002868"/>
        <path d="M0,0 L24,16 M24,0 L0,16" stroke="#fff" strokeWidth="2"/>
        <path d="M0,0 L24,16 M24,0 L0,16" stroke="#bf0a30" strokeWidth="1"/>
        <rect x="10" width="4" height="16" fill="#fff"/>
        <rect y="6" width="24" height="4" fill="#fff"/>
        <rect x="11" width="2" height="16" fill="#bf0a30"/>
        <rect y="7" width="24" height="2" fill="#bf0a30"/>
      </svg>
    )
  }
];

export const LanguageSelector = ({ currentLanguage, onLanguageChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const currentLang = languages.find(l => l.code === currentLanguage) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded hover:border-[#00D4FF]/50 transition-colors"
      >
        {currentLang.flag}
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-full mt-2 bg-[#0a0c10] border border-white/10 rounded shadow-xl z-50 overflow-hidden"
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  onLanguageChange(lang.code);
                  setIsOpen(false);
                }}
                className={`flex items-center gap-3 w-full px-4 py-3 hover:bg-[#00D4FF]/10 transition-colors ${
                  lang.code === currentLanguage ? 'bg-[#00D4FF]/5' : ''
                }`}
              >
                {lang.flag}
                <span className="text-sm text-white whitespace-nowrap">{lang.name}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
